import { displayDigits } from '../utils/displayDigits';
import i18n from 'i18next';
import React, { useEffect, useRef } from 'react';
import { useLocale } from '../hooks/useLocale';

export interface WizardStepDef {
  title: string;
  status: 'done' | 'current' | 'upcoming';
}

interface NmWizardProps {
  steps: WizardStepDef[];
  style?: React.CSSProperties;
}

/** Horizontal step indicator for multi-step flows (register, onboarding). */
export function NmWizard({ steps, style }: NmWizardProps) {
  const { isRtl } = useLocale();
  const dir = isRtl ? 'rtl' : 'ltr';
  const scrollRef = useRef<HTMLDivElement>(null);
  const currentStepRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = scrollRef.current;
    const stepEl = currentStepRef.current;
    if (!container || !stepEl) return;
    if (container.scrollWidth <= container.clientWidth) return;

    const targetLeft = stepEl.offsetLeft - (container.clientWidth - stepEl.offsetWidth) / 2;
    container.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
  }, [steps, isRtl]);

  return (
    <div className="nm-wizard-scroll" ref={scrollRef} dir={dir}>
      <div
        className="nm-wizard"
        style={style}
        role="list"
        aria-label={i18n.t('register.progress')}
        dir={dir}
      >
      {steps.map((step, index) => (
        <React.Fragment key={`${step.title}-${index}`}>
          <div
            ref={step.status === 'current' ? currentStepRef : undefined}
            className={`nm-wizard-step nm-wizard-step--${step.status}`}
            role="listitem"
            aria-current={step.status === 'current' ? 'step' : undefined}
          >
            <div className="nm-wizard-dot">
              {step.status === 'done' ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <span>{displayDigits(index + 1)}</span>
              )}
            </div>
            <span className="nm-wizard-label">{step.title}</span>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`nm-wizard-connector ${step.status === 'done' ? 'nm-wizard-connector--done' : ''}`}
              aria-hidden
            />
          )}
        </React.Fragment>
      ))}
      </div>
    </div>
  );
}
